import { useEffect, useState, useRef } from 'react';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type Plan from '@alltrails/core/types/Plan';
import { logError, executeRecaptcha } from '@alltrails/core';
import type { ApplePayInstance, RecurlyError, ApplePayConfig } from '@recurly/recurly-js';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import { ApiError } from '@alltrails/shared/api';
import type Coupon from '@alltrails/types/Coupon';
import type ProductType from '@alltrails/amplitude/enums/ProductType';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import classNames from 'classnames';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import type ProResponse from '../../types/ProResponse';
import { getPriceTotal } from '../../utils/plan';
import useLoadApplePaySDK from '../../hooks/useLoadApplePaySDK';
import styles from './ApplePayButtonWithSDK.module.scss';

type Props = {
  applePayConfig: ApplePayConfig;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  onApplePayToken: ({ apple_pay_token, recaptcha }: { apple_pay_token: string; recaptcha: string }) => void;
  plan: Plan;
  roundedCorners?: boolean;
  setErrors: (errors: ProResponse['errors']) => void;
  setSubmitting: (submitting: boolean) => void;
  showContinueWith?: boolean;
  productType: ProductType;
  webProPurchaseLocation: WebProPurchaseLocation;
};

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1
export default function ApplePayButtonWithSDK({
  applePayConfig,
  coupon,
  currencyCode,
  onApplePayToken,
  plan,
  productType,
  roundedCorners = false,
  setErrors,
  setSubmitting,
  showContinueWith = false,
  webProPurchaseLocation
}: Props) {
  const [canDisplayApplePayButton, setCanDisplayApplePayButton] = useState<boolean | null>(null);
  const [applePayInstance, setApplePayInstance] = useState<ApplePayInstance>();
  const [applePayReady, setApplePayReady] = useState(false);

  const experiments = useExperiments();
  const sendExposure = useExposureEvent('web-growth-rm-apple-pay-recurring');
  const languageRegionCode = useLanguageRegionCode();

  const isApplePaySDKLoaded = useLoadApplePaySDK(true);

  useEffect(() => {
    if (isApplePaySDKLoaded) {
      if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
        setCanDisplayApplePayButton(true);
      } else {
        setCanDisplayApplePayButton(false);
      }
    }
  }, [isApplePaySDKLoaded]);

  useEffect(() => {
    // https://github.com/recurly/recurly-js/blob/4e2789fd7e5a8e07e8dcd5a204a25ca92894ba68/lib/recurly/apple-pay/apple-pay.js#L57
    if (isApplePaySDKLoaded && window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);

      const applePay = recurly.ApplePay({
        country: 'US', // this is the merchant's country code, so will remain US
        currency: currencyCode,
        label: 'AllTrails',
        ...(experiments['web-growth-rm-apple-pay-recurring']?.value === 'treatment' ? applePayConfig : { total: String(getPriceTotal(plan, coupon)) })
      });

      applePay.on('error', (error: RecurlyError) => {
        logError(error);
        logPurchaseFailed({ error_code: error.message, payment_method: 'apple_pay' });
      });

      applePay.on('token', async token => {
        try {
          setSubmitting(true);

          const recaptcha = await executeRecaptcha('userPaymentAction');

          await onApplePayToken({ apple_pay_token: token.id, recaptcha });
        } catch (e) {
          logPurchaseFailed({
            ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
            payment_method: 'apple_pay'
          });

          if (e instanceof ApiError) {
            setErrors(e.data);
          }

          setSubmitting(false);
        }
      });

      applePay.ready(() => {
        setApplePayReady(true);
      });

      setApplePayInstance(applePay);
    }
  }, [currencyCode, coupon, onApplePayToken, plan, setErrors, setSubmitting, applePayConfig, experiments, isApplePaySDKLoaded]);

  const applePayButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const applePayButton = applePayButtonRef.current;

    if (applePayButton) {
      const handleClick = () => {
        sendExposure();
        logPurchaseButtonTapped({ payment_method: 'apple_pay', product_type: productType, web_purchase_location: webProPurchaseLocation });

        if (applePayReady && applePayInstance) {
          applePayInstance.begin();
        }
      };

      applePayButton.addEventListener('click', handleClick);

      return () => {
        if (applePayButton) {
          applePayButton.removeEventListener('click', handleClick);
        }
      };
    }
    return undefined;
  }, [applePayReady, applePayInstance, productType, webProPurchaseLocation, sendExposure, isApplePaySDKLoaded]);

  // Show placeholder only while SDK is loading
  if (canDisplayApplePayButton === null) {
    return <div className={styles.placeholder} />;
  }

  if (canDisplayApplePayButton) {
    return (
      <div className={classNames(styles.applePayButtonContainer, { [styles.roundedCorners]: roundedCorners })}>
        {/* @ts-ignore - apple-pay-button is a custom element required by the Apple Pay SDK */}
        <apple-pay-button
          buttonstyle="black"
          type={showContinueWith ? 'continue' : 'plain'}
          // Full locale support https://developer.apple.com/documentation/applepayontheweb/applepaybuttonlocale
          locale={languageRegionCode}
          ref={applePayButtonRef}
          // @ts-ignore - apple-pay-button is a custom element required by the Apple Pay SDK
        ></apple-pay-button>
      </div>
    );
  }

  return null;
}
